import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useContacts } from '../lib/ContactsContext';
import { useAuth } from '../lib/auth';
import NameCleanupPrompt from '../components/contacts/NameCleanupPrompt';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Search, X, SlidersHorizontal, Sparkles, GitMerge } from 'lucide-react';
import { fuzzyTokenMatch, maxEditsForToken, relevanceScore, suggestCorrection } from '../lib/fuzzy';
import { findDuplicateClusters } from '../lib/duplicates';
import SearchFiltersBar from '../components/search/SearchFiltersBar';
import SearchResultsGrid from '../components/search/SearchResultsGrid';
import SearchAZSidebar from '../components/search/SearchAZSidebar';
import SearchEmptyState from '../components/search/SearchEmptyState';
import ContactDetailPane from '../components/search/ContactDetailPane';
import { ScaledBlock, usePlainChrome } from '../components/layout/ContentScale';

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export default function SearchPage() {
  usePlainChrome();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [selectedCities, setSelectedCities] = useState(
    // Session 124x — when the user lands on /search from the Places
    // map (`?city=NYC`), pre-apply that as a city filter so the
    // results render as "Contacts in NYC" instead of the whole list.
    (searchParams.get('city') || '').trim()
      ? [(searchParams.get('city') || '').trim()]
      : []
  );
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [favOnly, setFavOnly] = useState(searchParams.get('favorite') === 'true');
  const [sortBy, setSortBy] = useState('first_name');
  const [contacts, setContacts] = useState([]);
  const [allContacts, setAllContacts] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [allOrgs, setAllOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [activeLetter, setActiveLetter] = useState(null);
  const [totalContacts, setTotalContacts] = useState(0);
  const resultsRef = useRef(null);
  const letterRefs = useRef({});
  const searchInputRef = useRef(null);

  const { contacts: ctxContacts, groups: ctxGroups, loaded: ctxLoaded, refreshIfStale } = useContacts();
  const [groups, setGroups] = useState([]);

  // Subtle "possible duplicates" nudge — high-precision (shared phone+name /
  // email) detection over the loaded contacts. Taps deep-link into the Manage
  // tab's Compare dialog, pre-loaded with the largest duplicate set.
  const dupeCount = useMemo(() => findDuplicateClusters(ctxContacts).length, [ctxContacts]);

  // Desktop master–detail (≥1024px): a selectable list on the left + the
  // selected contact's summary on the right, instead of a full-page reload.
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches,
  );
  const [selectedId, setSelectedId] = useState(null);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Auto-focus the search input on mount AND every time the user
  // arrives with `?focus=true` (the home-page search-bar tap appends
  // it). Belt-and-suspenders on top of the React `autoFocus` prop —
  // some browsers fire `autoFocus` before the keyboard layout settles
  // and the caret ends up off-screen; an extra `.focus()` on the next
  // animation frame nails it.
  const focusSearch = searchParams.get('focus');
  useEffect(() => {
    requestAnimationFrame(() => {
      searchInputRef.current?.focus({ preventScroll: true });
    });
    // Strip the param so subsequent re-renders don't keep re-focusing.
    if (focusSearch) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('focus');
        return next;
      }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusSearch]);

  // Sync from context — all search/filter runs against local data
  useEffect(() => {
    refreshIfStale();
  }, [refreshIfStale]);

  useEffect(() => {
    if (!ctxLoaded) return;
    setGroups(ctxGroups);
    setAllContacts(ctxContacts);
    setTotalContacts(ctxContacts.length);
    // Extract filter options
    const cities = new Set();
    const orgs = new Set();
    ctxContacts.forEach((c) => {
      c.addresses?.forEach((a) => { if (a.city) cities.add(a.city); });
      const company = c.work?.company || c.company;
      if (company) orgs.add(company);
    });
    setAllCities([...cities].sort());
    setAllOrgs([...orgs].sort());
    setLoading(false);
  }, [ctxContacts, ctxGroups, ctxLoaded]);

  // Client-side search (instant, no network)
  useEffect(() => {
    if (!ctxLoaded) return;
    let result = ctxContacts;
    if (query) {
      // Tokenised prefix matching. The query is split into tokens and each
      // token must match the START of at least one token in the searchable
      // text — so "Tim Bu" finds "Timothy Burns" (was previously broken
      // because "tim bu" never appears contiguously in any contact field).
      //
      // Phone numbers get a special case: if the query token is all digits
      // we also accept a SUBSTRING match inside any searchable token, so
      // "555" still finds "+15553334444" even though the canonical phone
      // doesn't START with "555".
      const qTokens = query.toLowerCase().split(/\s+/).filter(Boolean);
      result = result.filter((c) => {
        const searchableText = [
          c.first_name, c.last_name, c.middle_name, c.nickname, c.prefix, c.suffix,
          ...(c.emails || []).map((e) => e.email),
          ...(c.phones || []).map((p) => p.number),
          ...(c.addresses || []).map((a) => [a.street, a.city, a.state, a.zip, a.country].join(' ')),
          c.work?.company, c.work?.title, c.organization, c.school, c.notes,
          // How we met (incl. date) — auto-populated / edited fields
          c.how_we_met?.at_location, c.how_we_met?.via_person,
          c.how_we_met?.description, c.how_we_met?.date,
          // Creation context (location + timestamp) — searchable for "where"/"when"
          c.created_place_name, c.created_context_text, c.created_plus_code, c.created_at_utc,
          c.birthday,
          ...(c.tags || []),
          ...(c.groups || []),
          ...(c.websites || []).map((w) => w.url),
          ...(c.social_profiles || []).map((s) => `${s.username || ''} ${s.url || ''}`),
        ].filter(Boolean).join(' ').toLowerCase();
        // Split searchable into tokens on whitespace AND a few separators
        // common in emails/urls (@, ., /, _, -) so "smith" inside
        // "john.smith@x.com" is reachable as a prefix.
        const sTokens = searchableText.split(/[\s@./_-]+/).filter(Boolean);
        return qTokens.every((qt) => {
          // Prefix + digit-substring + Levenshtein typo-tolerance in one
          // shot. `maxEditsForToken` returns 0 for very short tokens, so
          // "li" still behaves like a strict prefix match — we only loosen
          // tolerance once the query is long enough that typos start to
          // dominate the false-negative rate (3+ chars).
          const budget = maxEditsForToken(qt);
          return sTokens.some((st) => fuzzyTokenMatch(qt, st, budget));
        });
      });
    }
    if (favOnly) {
      result = result.filter((c) => c.is_favorite);
    }
    setContacts(result);
  }, [query, favOnly, ctxContacts, ctxLoaded]);

  // Multi-select filter: OR within type, AND across types
  const filteredByFilters = useMemo(() => {
    let result = contacts;
    if (selectedCities.length > 0) {
      result = result.filter((c) => c.addresses?.some((a) => selectedCities.includes(a.city)));
    }
    if (selectedGroups.length > 0) {
      result = result.filter((c) => selectedGroups.some((g) => c.groups?.includes(g)));
    }
    if (selectedOrgs.length > 0) {
      result = result.filter((c) => selectedOrgs.includes(c.work?.company) || selectedOrgs.includes(c.company));
    }
    return result;
  }, [contacts, selectedCities, selectedGroups, selectedOrgs]);

  const clearFilters = () => {
    setQuery('');
    setSelectedCities([]);
    setSelectedGroups([]);
    setSelectedOrgs([]);
    setFavOnly(false);
    setShowFilters(false);
    setSearchParams({});
  };

  const activeFilterCount = [
    selectedCities.length > 0,
    selectedGroups.length > 0,
    selectedOrgs.length > 0,
    favOnly,
  ].filter(Boolean).length;

  // "Did you mean: Steven Gorman?" — surfaces only when the current query
  // relied on the fuzzy fallback. Computed against the post-filter result
  // set so it stays consistent with what the user actually sees.
  const suggestion = useMemo(() => {
    if (!query.trim()) return null;
    return suggestCorrection(query, filteredByFilters);
  }, [query, filteredByFilters]);

  // Sort contacts
  const sortedContacts = useMemo(() => {
    const q = query.trim();
    const sorted = [...filteredByFilters];
    sorted.sort((a, b) => {
      // Active search: highest-probability EXACT match first, before the
      // chosen A–Z / city / org ordering.
      if (q) {
        const rel = relevanceScore(q, a) - relevanceScore(q, b);
        if (rel !== 0) return rel;
      }
      switch (sortBy) {
        case 'first_name': {
          const aName = (a.first_name || a.last_name || a.work?.company || a.organization || '').toLowerCase();
          const bName = (b.first_name || b.last_name || b.work?.company || b.organization || '').toLowerCase();
          return aName.localeCompare(bName);
        }
        case 'last_name':
          return (a.last_name || '').localeCompare(b.last_name || '');
        case 'company': {
          const aOrg = (a.work?.company || a.company || '').toLowerCase();
          const bOrg = (b.work?.company || b.company || '').toLowerCase();
          if (!aOrg && bOrg) return 1;
          if (aOrg && !bOrg) return -1;
          return aOrg.localeCompare(bOrg);
        }
        case 'city': {
          const aCity = (a.addresses?.[0]?.city || '').toLowerCase();
          const bCity = (b.addresses?.[0]?.city || '').toLowerCase();
          if (!aCity && bCity) return 1;
          if (aCity && !bCity) return -1;
          return aCity.localeCompare(bCity);
        }
        default:
          return 0;
      }
    });
    return sorted;
  }, [filteredByFilters, sortBy, query]);

  // Group contacts — use full city/org for those sort modes, first letter
  // otherwise. During an active text search we collapse everything into one
  // relevance-ordered "Top matches" group so the best match sits at the top.
  const contactsByGroup = useMemo(() => {
    if (query.trim()) return { 'Top matches': sortedContacts };
    const grouped = {};
    sortedContacts.forEach((contact) => {
      let key;
      if (sortBy === 'city') {
        key = contact.addresses?.[0]?.city || 'No City';
      } else if (sortBy === 'company') {
        key = contact.work?.company || contact.company || 'No Organization';
      } else if (sortBy === 'last_name') {
        key = (contact.last_name?.[0] || '#').toUpperCase();
      } else {
        const sortName = contact.first_name || contact.last_name || contact.work?.company || contact.organization || '';
        key = (sortName[0] || '#').toUpperCase();
      }
      if (sortBy !== 'city' && sortBy !== 'company' && !ALPHABET.includes(key)) key = '#';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(contact);
    });
    return grouped;
  }, [sortedContacts, sortBy, query]);

  // Ordered group keys
  const groupKeys = useMemo(() => {
    if (query.trim()) return ['Top matches'];
    const keys = Object.keys(contactsByGroup);
    if (sortBy === 'city' || sortBy === 'company') {
      const emptyKey = sortBy === 'city' ? 'No City' : 'No Organization';
      return keys.filter((k) => k !== emptyKey).sort((a, b) => a.localeCompare(b))
        .concat(keys.includes(emptyKey) ? [emptyKey] : []);
    }
    return ALPHABET.filter((l) => contactsByGroup[l]?.length > 0)
      .concat(contactsByGroup['#'] ? ['#'] : []);
  }, [contactsByGroup, sortBy, query]);

  // Progressive rendering — show groups in batches as the user scrolls
  const INITIAL_GROUPS = 6;
  const [visibleGroupCount, setVisibleGroupCount] = useState(INITIAL_GROUPS);
  const sentinelRef = useRef(null);

  useEffect(() => {
    setVisibleGroupCount(INITIAL_GROUPS);
  }, [groupKeys.length, sortBy, query, selectedCities.length, selectedGroups.length, selectedOrgs.length, favOnly]);

  useEffect(() => {
    if (visibleGroupCount >= groupKeys.length) return;
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisibleGroupCount((prev) => Math.min(prev + 6, groupKeys.length)); },
      { rootMargin: '200px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visibleGroupCount, groupKeys.length]);

  const visibleKeys = groupKeys.slice(0, visibleGroupCount);

  // Scroll to a letter section, expanding the progressive list if needed.
  const scrollToLetter = (letter) => {
    setActiveLetter(letter);
    const idx = groupKeys.indexOf(letter);
    if (idx >= visibleGroupCount) {
      setVisibleGroupCount(idx + 3);
    }
    setTimeout(() => {
      letterRefs.current[letter]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const selectedContact = useMemo(
    () => (ctxContacts || []).find((c) => c.id === selectedId) || null,
    [ctxContacts, selectedId],
  );
  // Keep a valid selection on desktop: default to the first result and
  // re-point if the current selection drops out of the filtered set.
  useEffect(() => {
    if (!isDesktop) return;
    if (sortedContacts.length === 0) { setSelectedId(null); return; }
    if (!selectedId || !sortedContacts.some((c) => c.id === selectedId)) {
      setSelectedId(sortedContacts[0].id);
    }
  }, [isDesktop, sortedContacts, selectedId]);

  const goToAddContact = () => navigate('/add');
  const showAZ = (sortBy === 'first_name' || sortBy === 'last_name') && !query.trim();
  const hasFilters = selectedCities.length > 0 || selectedGroups.length > 0 || selectedOrgs.length > 0 || favOnly;

  return (
    <div className="p-4 space-y-4 page-enter" data-testid="search-page">
      <ScaledBlock>
      <div className="space-y-4">
      {/* Header with Search Bar and Add Button */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search contacts, places, dates…"
            className="h-12 pl-10 pr-16 bg-card border-border focus:border-nav-blue rounded-xl shadow-sm"
            data-testid="search-input"
            autoFocus
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            {query && (
              <button onClick={() => setQuery('')} className="p-1 text-muted-foreground hover:text-foreground" data-testid="search-clear">
                <X size={16} />
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg transition-colors relative ${showFilters ? 'bg-nav-blue text-white' : 'text-muted-foreground hover:text-foreground'}`}
              data-testid="search-filter-toggle"
            >
              <SlidersHorizontal size={16} />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-signal-yellow rounded-full text-[9px] text-nav-blue font-bold flex items-center justify-center">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <Button
          onClick={() => navigate('/manage-contacts')}
          className="hidden lg:inline-flex h-12 px-4 rounded-xl shrink-0"
          variant="outline"
          data-testid="search-manage-contacts-btn"
        >
          Manage contacts
        </Button>
      </div>
      <Button
        onClick={() => navigate('/manage-contacts')}
        className="lg:hidden w-full h-12 rounded-xl"
        variant="outline"
        data-testid="search-manage-contacts-narrow-btn"
      >
        Manage contacts
      </Button>

      {showFilters && (
        <SearchFiltersBar
          allCities={allCities}
          selectedCities={selectedCities}
          setSelectedCities={setSelectedCities}
          groups={groups}
          selectedGroups={selectedGroups}
          setSelectedGroups={setSelectedGroups}
          allOrgs={allOrgs}
          selectedOrgs={selectedOrgs}
          setSelectedOrgs={setSelectedOrgs}
          favOnly={favOnly}
          setFavOnly={setFavOnly}
          onClear={clearFilters}
        />
      )}

      {/* Results header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {filteredByFilters.length} contact{filteredByFilters.length !== 1 ? 's' : ''}
          {totalContacts > allContacts.length && ` of ${totalContacts}`}
          {hasFilters && ' (filtered)'}
          {totalContacts > allContacts.length && <span className="ml-1 text-xs text-nav-blue">(loading...)</span>}
        </p>
        <div className="flex items-center gap-2">
          {allContacts.length > 0 && filteredByFilters.length === 0 && hasFilters && (
            <button onClick={clearFilters} className="text-xs text-nav-blue hover:underline">
              Show all
            </button>
          )}
          <div className="flex items-center gap-1">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-8 text-xs w-[120px] border border-border rounded-md bg-background px-2 py-1 outline-none focus:ring-1 focus:ring-ring"
              data-testid="sort-select"
            >
              <option value="first_name">First Name</option>
              <option value="last_name">Last Name</option>
              <option value="company">Organization</option>
              <option value="city">City</option>
            </select>
          </div>
        </div>
      </div>

      {!query.trim() && !hasFilters && (
        <NameCleanupPrompt contacts={ctxContacts} selfId={user?.my_contact_id} />
      )}

      {dupeCount > 0 && !query.trim() && !hasFilters && (
        <button
          type="button"
          onClick={() => {
            navigate('/manage-contacts/list?dupes=1');
          }}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl bg-nav-blue/10 hover:bg-nav-blue/15 transition-colors -mt-1"
          data-testid="search-dupe-nudge"
        >
          <span className="w-6 h-6 rounded-full bg-card flex items-center justify-center shrink-0">
            <GitMerge size={14} className="text-nav-blue" />
          </span>
          <span className="text-sm font-semibold text-foreground flex-1 text-left">
            {dupeCount === 1 ? '1 possible duplicate' : `${dupeCount} possible duplicates`}
          </span>
          <span className="text-sm font-bold text-nav-blue">Review &amp; merge →</span>
        </button>
      )}

      {/* Did-you-mean — tap rewrites the query in-place. */}
      {suggestion && suggestion.correctedQuery.toLowerCase() !== query.trim().toLowerCase() && (
        <button
          type="button"
          onClick={() => setQuery(suggestion.correctedQuery)}
          className="flex items-center gap-2 text-sm text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 transition-colors -mt-1"
          data-testid="search-did-you-mean"
        >
          <Sparkles size={14} className="text-amber-500 shrink-0" />
          <span>
            Did you mean
            <span className="font-semibold underline decoration-dotted ml-1" data-testid="search-did-you-mean-value">
              {suggestion.correctedQuery}
            </span>
            <span className="text-amber-700/70 dark:text-amber-300/70">?</span>
          </span>
        </button>
      )}

      </div>
      </ScaledBlock>

      {/* Results */}
      {loading || contacts.length === 0 ? (
        <ScaledBlock>
        <SearchEmptyState
          loading={loading}
          hasNoContactsAtAll={allContacts.length === 0}
          onAdd={goToAddContact}
        />
        </ScaledBlock>
      ) : isDesktop ? (
        <div className="flex gap-6 items-start" data-testid="search-split">
          <div className="w-[360px] xl:w-[400px] shrink-0">
            <SearchResultsGrid
              ref={resultsRef}
              layout="list"
              selectedId={selectedId}
              onSelect={setSelectedId}
              visibleKeys={visibleKeys}
              contactsByGroup={contactsByGroup}
              letterRefs={letterRefs}
              sentinelRef={sentinelRef}
              showSentinel={visibleGroupCount < groupKeys.length}
              sortBy={sortBy}
            />
          </div>
          <div className="flex-1 min-w-0 sticky top-2 self-start max-h-[calc(100dvh-150px)] overflow-y-auto">
            <ScaledBlock>
            <ContactDetailPane
              contact={selectedContact}
              groups={ctxGroups}
              onOpenFull={() => selectedContact && navigate(`/contacts/${selectedContact.id}`)}
            />
            </ScaledBlock>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <SearchResultsGrid
            ref={resultsRef}
            visibleKeys={visibleKeys}
            contactsByGroup={contactsByGroup}
            letterRefs={letterRefs}
            sentinelRef={sentinelRef}
            showSentinel={visibleGroupCount < groupKeys.length}
            sortBy={sortBy}
          />
          {showAZ && (
            <SearchAZSidebar
              contactsByGroup={contactsByGroup}
              activeLetter={activeLetter}
              onLetterClick={scrollToLetter}
            />
          )}
        </div>
      )}
    </div>
  );
}
